import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";
import axios from "axios";
import safeStorage from "@/lib/safeStorage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "tc_token";

const AuthContext = createContext({
    user: null,
    token: null,
    loading: true,
    login: async () => {},
    signup: async () => {},
    loginGoogle: async () => {},
    logout: () => {},
});

export function AuthProvider({ children }) {
    const [token, setToken] = useState(() => safeStorage.getItem(TOKEN_KEY));
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchMe = useCallback(async (t) => {
        try {
            const res = await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${t}` },
            });
            setUser(res.data);
            return res.data;
        } catch (e) {
            console.warn("[auth] /me failed; clearing token", e?.response?.status);
            safeStorage.removeItem(TOKEN_KEY);
            setToken(null);
            setUser(null);
            return null;
        }
    }, []);

    // CRITICAL: If returning from Emergent OAuth callback, the AuthCallback page
    // will exchange the session_id and call setSession() — skip /me here so we
    // don't 401 on a token that hasn't been minted yet.
    useEffect(() => {
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        if (!token) {
            setLoading(false);
            return;
        }
        fetchMe(token).finally(() => setLoading(false));
    }, [token, fetchMe]);

    const setSession = useCallback((accessToken, userObj) => {
        safeStorage.setItem(TOKEN_KEY, accessToken);
        setToken(accessToken);
        setUser(userObj);
    }, []);

    const login = useCallback(
        async (email, password) => {
            const res = await axios.post(`${API}/auth/login`, { email, password });
            setSession(res.data.access_token, res.data.user);
            return res.data.user;
        },
        [setSession],
    );

    const signup = useCallback(
        async (email, password, name) => {
            const res = await axios.post(`${API}/auth/signup`, {
                email,
                password,
                name,
            });
            setSession(res.data.access_token, res.data.user);
            return res.data.user;
        },
        [setSession],
    );

    const loginGoogle = useCallback(
        async (sessionId) => {
            const res = await axios.post(`${API}/auth/google`, {
                session_id: sessionId,
            });
            setSession(res.data.access_token, res.data.user);
            return res.data.user;
        },
        [setSession],
    );

    const logout = useCallback(() => {
        safeStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setUser(null);
        // Best-effort server signal; ignore errors.
        axios.post(`${API}/auth/logout`).catch(() => {});
    }, []);

    return (
        <AuthContext.Provider
            value={{ user, token, loading, login, signup, loginGoogle, logout, persistSession: setSession }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}

/** Convenience axios config for authed calls. */
export function authHeaders(token) {
    return token ? { Authorization: `Bearer ${token}` } : {};
}
